"use client";

import { IconChevronDownOutline18, IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import { memo, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Matches tool context headers produced by the Go backend (tool_context.go).
// Supports both old format [Actions for ...] and new format [TOOL-CONTEXT: Actions for ...]
// Keep in sync with FormatContext() methods in services/b3os-workflow/internal/app/agent/tool_context.go
const TOOL_CONTEXT_HEADER_RE =
  /^\[(?:TOOL-CONTEXT:\s*)?(?:Web search|Actions? for|Triggers? for|Trigger schema|Schema|Sheet|Balance|Price|DeFi|Search (?:templates|recipes)|Token|Portfolio|Wallet|NFT|Coinglass|Morpho|Polymarket|Dune discovery|Knowledge|Workflows|Run)[\s:|\]]/i;

function parseMarkdownIntoBlocks(markdown: string): string[] {
  const blocks: string[] = [];
  const lines = markdown.split("\n");
  let currentBlock: string[] = [];
  let inTable = false;
  let inToolContext = false;
  // Track whether we're inside a list so blank lines between loose list items
  // don't split the list into separate ReactMarkdown instances (which would each
  // render starting from "1." instead of continuing the counter).
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trim();
    const isTableRow = trimmed.startsWith("|") && trimmed.endsWith("|");
    const isSeparator = /^\|[\s\-:|]+\|$/.test(trimmed);
    const isToolCtxHeader = TOOL_CONTEXT_HEADER_RE.test(trimmed);
    const isToolCtxContinuation = inToolContext && trimmed !== "" && !isToolCtxHeader;
    const isListItem = /^(\d+\.|[-*+])\s/.test(trimmed);
    const isIndented = line.startsWith("  ") || line.startsWith("\t");

    if (isTableRow || isSeparator) {
      if (!inTable && currentBlock.length > 0) {
        blocks.push(currentBlock.join("\n"));
        currentBlock = [];
      }
      inTable = true;
      inToolContext = false;
      inList = false;
      currentBlock.push(line);
    } else if (isToolCtxHeader) {
      if (currentBlock.length > 0) {
        blocks.push(currentBlock.join("\n"));
        currentBlock = [];
      }
      inTable = false;
      inToolContext = true;
      inList = false;
      currentBlock.push(line);
    } else if (isToolCtxContinuation) {
      currentBlock.push(line);
    } else {
      // Leaving a table or tool context — flush
      if ((inTable || inToolContext) && currentBlock.length > 0) {
        blocks.push(currentBlock.join("\n"));
        currentBlock = [];
        inTable = false;
        inToolContext = false;
      }

      if (trimmed === "") {
        if (inList) {
          // Preserve blank lines within a list block so loose lists stay together.
          currentBlock.push(line);
        } else if (currentBlock.length > 0) {
          blocks.push(currentBlock.join("\n"));
          currentBlock = [];
        }
      } else {
        // A non-blank, non-list, non-indented line ends the list context.
        if (inList && !isListItem && !isIndented) {
          // Trim trailing blank lines accumulated while waiting for the next list item.
          while (currentBlock.length > 0 && currentBlock[currentBlock.length - 1].trim() === "") {
            currentBlock.pop();
          }
          if (currentBlock.length > 0) {
            blocks.push(currentBlock.join("\n"));
            currentBlock = [];
          }
          inList = false;
        }
        if (isListItem) {
          inList = true;
        }
        currentBlock.push(line);
      }
    }
  }

  if (currentBlock.length > 0) {
    // Trim trailing blank lines from the last block.
    while (currentBlock.length > 0 && currentBlock[currentBlock.length - 1].trim() === "") {
      currentBlock.pop();
    }
    if (currentBlock.length > 0) {
      blocks.push(currentBlock.join("\n"));
    }
  }

  return blocks.filter(block => block.trim());
}

function ToolContextCard({ content }: { content: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const lines = content.trim().split("\n");
  const headerLine = lines[0];
  const resultLines = lines.slice(1).filter(l => l.trim());

  // Strip brackets and TOOL-CONTEXT prefix for clean display
  const headerText = headerLine.replace(/^\[(?:TOOL-CONTEXT:\s*)?/, "").replace(/\]$/, "");

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200/80 bg-zinc-50/80 text-xs dark:border-zinc-700/80 dark:bg-zinc-800/40">
      <button
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        className="text-ds-text-disabled dark:text-ds-text-muted flex w-full items-center gap-2 px-3 py-1.5 text-left font-medium transition-colors hover:bg-zinc-100/70 dark:hover:bg-zinc-800/60"
      >
        {isExpanded ? (
          <IconChevronDownOutline18 className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <IconChevronRightOutline18 className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="line-clamp-1 min-w-0 flex-1 truncate">{headerText}</span>
      </button>
      {isExpanded && resultLines.length > 0 && (
        <div className="border-t border-zinc-200/60 dark:border-zinc-700/60">
          {resultLines.map((line, i) => {
            // Parse numbered results: "1. Title (URL) — Snippet"
            const match = line.trim().match(/^\d+\.\s+(.+?)\s*\((https?:\/\/\S+?)\)\s*(?:—\s*(.*))?$/);
            if (match) {
              const [, title, rawUrl, snippet] = match;
              const isTruncated = rawUrl.endsWith("...");
              const href = !isTruncated ? rawUrl : undefined;
              return (
                <div
                  key={i}
                  className="border-t border-zinc-100/80 px-3 py-1.5 first:border-t-0 dark:border-zinc-800/80"
                >
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {title.split(":")[1] || title}
                    </a>
                  ) : (
                    <span className="text-ds-text-secondary font-medium">{title.split(":")[1] || title}</span>
                  )}
                  {snippet && (
                    <p className="text-ds-text-disabled dark:text-ds-text-muted mt-0.5 leading-snug">{snippet}</p>
                  )}
                </div>
              );
            }
            // Fallback for non-numbered result lines
            return (
              <div
                key={i}
                className="text-ds-text-muted border-t border-zinc-100/80 px-3 py-1.5 first:border-t-0 dark:border-zinc-800/80"
              >
                {line.trim()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Shared react-markdown component overrides used across all markdown renderers.
 * Security: external links get rel="noopener noreferrer". No rehypeRaw — raw
 * HTML in markdown is stripped by default.
 */
export const markdownComponents: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  a: ({ href, ...props }) => {
    const isInternal = href && (href.startsWith("/") || href.startsWith(globalThis.location?.origin ?? ""));
    return (
      <a
        {...props}
        href={href}
        className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        target={isInternal ? "_self" : "_blank"}
        rel={isInternal ? undefined : "noopener noreferrer"}
      />
    );
  },
  code: ({ className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || "");
    const isInline = !match;

    if (isInline) {
      return (
        <code
          className="bg-ds-neutral-100 text-ds-text-primary dark:bg-ds-neutral-800 rounded px-1.5 py-0.5 font-mono text-sm dark:text-zinc-100"
          {...props}
        >
          {children}
        </code>
      );
    }

    return (
      <code
        className={`${className} bg-ds-neutral-100 dark:bg-ds-neutral-800 block rounded-lg p-3 font-mono text-sm`}
        {...props}
      >
        {children}
      </code>
    );
  },
  p: ({ children }) => <p className="leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-6">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-6">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  h1: ({ children }) => <h1 className="mb-2 mt-6 text-xl font-bold first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-2 mt-5 text-lg font-bold first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1.5 mt-4 text-base font-semibold first:mt-0">{children}</h3>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-ds-neutral-300 text-ds-text-subtle dark:text-ds-text-disabled border-l-4 pl-4 italic dark:border-zinc-600">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="border-ds-border-default my-2 overflow-x-auto rounded-lg border">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-ds-neutral-100 dark:bg-ds-neutral-800">{children}</thead>,
  tbody: ({ children }) => (
    <tbody className="divide-ds-neutral-200 dark:divide-ds-neutral-700 divide-y">{children}</tbody>
  ),
  tr: ({ children }) => <tr className="divide-ds-neutral-200 dark:divide-ds-neutral-700 divide-x">{children}</tr>,
  th: ({ children }) => (
    <th className="text-ds-text-secondary whitespace-nowrap px-3 py-2 text-left text-xs font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="text-ds-text-secondary px-3 py-2">{children}</td>,
};

const MemoizedMarkdownBlock = memo(
  ({ content }: { content: string }) => {
    if (TOOL_CONTEXT_HEADER_RE.test(content.trim())) {
      return <ToolContextCard content={content} />;
    }

    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.content === nextProps.content;
  },
);

MemoizedMarkdownBlock.displayName = "MemoizedMarkdownBlock";

export const MemoizedMarkdown = memo(({ content, id }: { content: string; id: string }) => {
  const blocks = useMemo(() => parseMarkdownIntoBlocks(content), [content]);

  return (
    <>
      {blocks.map((block, index) => (
        <MemoizedMarkdownBlock content={block} key={`${id}-block_${index}`} />
      ))}
    </>
  );
});

MemoizedMarkdown.displayName = "MemoizedMarkdown";
